import {
  AdaptivePointsMaterial,
  PotreeTree,
  WSKDTree,
  type FetcherClient,
  type ICadModel,
  type LodTreeNode,
  type PointCloudBufferGeometry,
} from "@faro-lotv/lotv";
import { Box3, Matrix4, Quaternion, Vector3 } from "three";
import type { PointCloudObject } from "@/object-cache";
import type { CadPointCloudAnalysis } from "@/store/point-cloud-analysis-tool-slice";
import type { ClashRun, ClashSettings } from "./cad-clash-detection";
import type {
  ClashWorkerRequest,
  ClashWorkerResponse,
} from "./cad-clash-worker";
import { CLASH_LIMITS } from "./cad-clash-detection";
import { sha256Hex } from "./cad-source-revision";
import { loadLodNode } from "@/utils/load-lod-node";
import {
  ClashJobControl,
  ClashNodeQueue,
  runClashQueue,
} from "./cad-clash-node-queue";
import { ClashWorkerClient, clashWorkerCount } from "./cad-clash-worker-client";
import { CROSSING_LIMITS } from "./cad-clash-surface-samples";
const DEFAULT_RESOLUTION = 0.005;
const MAX_DURATION = 120_000;
const CLASH_ALGORITHM_VERSION = 6;

/**
 *
 * @param analysis Current comparison settings.
 * @returns Frozen metric sampling and matching parameters.
 */
export function clashSettings(analysis: CadPointCloudAnalysis): ClashSettings {
  const clearance = analysis.clashClearance ?? 0;
  return {
    tolerance: analysis.tolerance,
    clearance,
    resolution: Math.min(
      DEFAULT_RESOLUTION,
      analysis.tolerance > 0 ? analysis.tolerance / 2 : DEFAULT_RESOLUTION,
      clearance > 0 ? clearance / 2 : DEFAULT_RESOLUTION,
    ),
  };
}

/**
 *
 * @param analysis Frozen reference and matching settings.
 * @param cloud Source scan metadata.
 * @param hiddenScans Sorted excluded scan IDs.
 * @returns Stable opaque identity for compatible review results.
 */
export function clashFingerprint(
  analysis: CadPointCloudAnalysis,
  cloud: PointCloudObject,
  hiddenScans: number[],
): Promise<string> {
  const data = JSON.stringify([
    CLASH_ALGORITHM_VERSION,
    analysis.cadReference,
    // Only data identity: metadata edits must not invalidate review decisions.
    [
      cloud.iElement.id,
      cloud.iElement.type,
      "uri" in cloud.iElement ? cloud.iElement.uri : undefined,
      "md5Hash" in cloud.iElement ? cloud.iElement.md5Hash : undefined,
    ],
    clashSettings(analysis),
    hiddenScans,
  ]);
  return sha256Hex(data);
}

/**
 *
 * @param cad Source model with original component acquisition.
 * @param cloud Source cloud and shared fetcher.
 * @param analysis Frozen comparison definition.
 * @param budget Existing CAD memory policy in bytes.
 * @param signal Owner cancellation signal.
 * @param progress Throttled UI progress callback.
 * @param control In-session pause/resume at bounded work boundaries.
 * @param concurrency Optional serial limits for validation and constrained callers.
 * @returns Bounded clash evidence and explicit sampling coverage.
 */
export async function runCadClashAnalysis(
  cad: ICadModel,
  cloud: PointCloudObject,
  analysis: CadPointCloudAnalysis,
  budget: number,
  signal: AbortSignal,
  progress: (message: string) => void,
  control = new ClashJobControl(),
  concurrency: { workers?: 1 | 2; prefetch?: 1 | 2 } = {},
): Promise<ClashRun> {
  const settings = clashSettings(analysis);
  const hiddenScans =
    cloud.material instanceof AdaptivePointsMaterial
      ? [...cloud.material.hiddenScans].sort((left, right) => left - right)
      : [];
  const fingerprint = await clashFingerprint(analysis, cloud, hiddenScans);
  const { default: ClashWorker } = await import("./cad-clash-worker?worker");
  signal.throwIfAborted();
  const worker = new ClashWorker();
  const workers = [worker];
  const owner = new AbortController();
  const combined = AbortSignal.any([signal, owner.signal]);
  const coordinator = new ClashWorkerClient(worker, combined);
  const storeName = `sphere-clash-work-${crypto.randomUUID()}`;
  const client: FetcherClient = {
    lodTreeFetcher: cloud.lodTreeFetcher,
    tree: cloud.tree,
  };
  const reasons = new Set<string>();
  const { cadReference } = analysis;
  const origin = cadReference.clipBox
    ? new Vector3().fromArray(cadReference.clipBox.position)
    : new Box3(
        new Vector3().fromArray(analysis.bounds[0]),
        new Vector3().fromArray(analysis.bounds[1]),
      ).getCenter(new Vector3());
  const scope = new Box3(
    new Vector3().fromArray(analysis.bounds[0]),
    new Vector3().fromArray(analysis.bounds[1]),
  ).expandByScalar(
    Math.max(
      settings.clearance,
      settings.tolerance * 5,
      CLASH_LIMITS.minimumBand,
    ),
  );
  const clipMatrix = cadReference.clipBox
    ? new Matrix4().compose(
        new Vector3().fromArray(cadReference.clipBox.position),
        new Quaternion().fromArray(cadReference.clipBox.rotation),
        new Vector3().fromArray(cadReference.clipBox.size).multiplyScalar(0.5),
      )
    : undefined;
  if (clipMatrix) {
    scope.copy(
      new Box3(new Vector3(-1, -1, -1), new Vector3(1, 1, 1)).applyMatrix4(
        clipMatrix,
      ),
    );
  }
  const clipInverse = clipMatrix?.clone().invert();
  const halo = CROSSING_LIMITS.radius + CLASH_LIMITS.groupingRadius;
  const loadScope = scope.clone().expandByScalar(halo);
  const matrix = new Matrix4().fromArray(cadReference.cloudTransform);
  const selectedIds = cadReference.all
    ? undefined
    : new Set(cadReference.objectIds);
  const relevant = new Set<number>();
  const componentBounds: Box3[] = [];
  let unknown = false;
  let componentId = 0;
  let unnamed = -1;
  let identified = false;
  let points = 0;
  let failedNodes = 0;
  const hidden = new Set(hiddenScans);
  const leases = new Set<number>();

  function send(request: ClashWorkerRequest): Promise<ClashWorkerResponse> {
    return coordinator.send(request);
  }

  client.lodTreeFetcher.addClient(client);
  try {
    const options = {
      origin,
      worldMatrix: new Matrix4().fromArray(cadReference.cadTransform),
      objectIds: selectedIds,
      maxBytes: Math.min(12 * 1024 * 1024, budget / 32),
      maxChunkBytes: budget,
      signal: combined,
    };
    progress("Locating CAD components");
    for await (const batch of cad.getAnalysisTriangles({
      ...options,
      // Parts lie inside their chunk, so chunks missing the scope hold no relevant part.
      // The boundary pass below still reads every chunk for complete components.
      includeChunk: (bounds) => bounds.intersectsBox(loadScope),
      includeComponent: (id, bounds) => {
        if (bounds.intersectsBox(loadScope)) {
          componentBounds.push(
            bounds
              .clone()
              .expandByScalar(
                Math.max(settings.clearance, settings.tolerance * 5, halo),
              ),
          );
          if (id === undefined) unknown = true;
          else relevant.add(id);
        }
        return false;
      },
    })) {
      void batch;
    }
    progress("Reading complete CAD boundaries");
    const geometry: Array<{
      positions: Float32Array;
      componentId: number;
      identified: boolean;
    }> = [];
    let geometryBytes = 0;
    for await (const positions of cad.getAnalysisTriangles({
      ...options,
      includeComponent: (id) => (id === undefined ? unknown : relevant.has(id)),
      onComponentBatch: (id) => {
        identified = id !== undefined;
        componentId = id ?? unnamed--;
      },
    })) {
      geometryBytes += positions.byteLength;
      geometry.push({ componentId, identified, positions });
    }
    const workerCount = Math.min(
      concurrency.workers ?? 2,
      clashWorkerCount(budget, geometryBytes, navigator.hardwareConcurrency),
    );
    const compute =
      workerCount === 1
        ? [coordinator]
        : Array.from({ length: workerCount }, () => {
            const worker = new ClashWorker();
            workers.push(worker);
            return new ClashWorkerClient(worker, combined);
          });
    for (const batch of geometry) {
      const copies = compute.map((_, index) =>
        index === 0 ? batch.positions : batch.positions.slice(),
      );
      await Promise.all(
        compute.map((client, index) =>
          client.send({
            operation: "geometry",
            ...batch,
            positions: copies[index],
          }),
        ),
      );
    }
    geometry.length = 0;
    progress(`Preparing clash queries (${compute.length} compute workers)`);
    await send({ operation: "prepare", settings, storeName });
    const prepared =
      compute.length === 1
        ? []
        : await Promise.all(
            compute.map((client) =>
              client.send({ operation: "prepare", settings, storeName }),
            ),
          );
    const nodeBounds = new Box3();
    const queue = new ClashNodeQueue(cloud.tree.root, (node) => {
      nodeBounds.copy(node.boundingBox).applyMatrix4(matrix);
      return (
        nodeBounds.intersectsBox(loadScope) &&
        componentBounds.some((bounds) => bounds.intersectsBox(nodeBounds))
      );
    });
    const point = new Vector3();
    const clipped = new Vector3();
    let requested = 0;
    const batchSize = 4096;
    const estimatedBytesPerPoint = 64;
    const prefetchBytes = Math.min(64 * 1024 * 1024, budget / 8);
    type LoadedNode =
      | { geometry: PointCloudBufferGeometry }
      | { error: unknown };
    await runClashQueue<LodTreeNode, LoadedNode>({
      next: () => queue.next(),
      concurrency: concurrency.prefetch ?? 2,
      control,
      signal: combined,
      maxWeight: prefetchBytes,
      weight: (node) =>
        node.numPoints > 0 ? node.numPoints * estimatedBytesPerPoint : Infinity,
      run: async (node) => {
        leases.add(node.id);
        try {
          return {
            geometry: await loadLodNode(
              client,
              node,
              AbortSignal.any([combined, AbortSignal.timeout(MAX_DURATION)]),
            ),
          };
        } catch (error) {
          if (combined.aborted) throw error;
          return { error };
        }
      },
      commit: async (node, loadedNode) => {
        let positions = new Float32Array(batchSize * 3);
        let eligible = new Uint8Array(batchSize);
        let count = 0;
        combined.throwIfAborted();
        requested++;
        progress(
          `Collecting scan data: ${points.toLocaleString()} samples, ${requested} nodes; ${queue.pending} discovered nodes pending`,
        );
        let spacing = Infinity;
        if (cloud.tree instanceof PotreeTree) {
          spacing = cloud.tree.spacing / 2 ** node.depth;
        } else if (cloud.tree instanceof WSKDTree) {
          spacing = node.pointDensity / 1000;
        }
        spacing *= matrix.getMaxScaleOnAxis();
        if (!Number.isFinite(spacing) || spacing <= 0) {
          spacing = Infinity;
          reasons.add("Sampling spacing is unavailable in some scan regions");
        }
        let loaded = false;
        try {
          if ("error" in loadedNode) throw loadedNode.error;
          const { geometry } = loadedNode;
          loaded = true;
          for (let index = 0; index < geometry.size; index++) {
            if (index % batchSize === 0) {
              await new Promise<void>((resolve) => setTimeout(resolve, 0));
              combined.throwIfAborted();
            }
            points++;
            if (hidden.has(geometry.attributes.scanIds?.getX(index) ?? 0)) {
              continue;
            }
            point
              .fromBufferAttribute(geometry.attributes.position, index)
              .applyMatrix4(matrix);
            if (
              !loadScope.containsPoint(point) ||
              !componentBounds.some((bounds) => bounds.containsPoint(point))
            ) {
              continue;
            }
            let owns = scope.containsPoint(point);
            if (clipInverse) {
              clipped.copy(point).applyMatrix4(clipInverse);
              if (
                Math.max(
                  Math.abs(clipped.x),
                  Math.abs(clipped.y),
                  Math.abs(clipped.z),
                ) > 1
              ) {
                owns = false;
              }
            }
            eligible[count] = Number(owns);
            point.sub(origin).toArray(positions, count * 3);
            if (++count === batchSize) {
              await send({ operation: "points", positions, spacing, eligible });
              positions = new Float32Array(batchSize * 3);
              eligible = new Uint8Array(batchSize);
              count = 0;
            }
          }
        } catch (error) {
          if (combined.aborted) throw error;
          if (loaded) throw error;
          failedNodes++;
          reasons.add("Some scan nodes could not be read");
        } finally {
          client.lodTreeFetcher.releaseNode(node.id, client);
          leases.delete(node.id);
        }
        if (count) {
          await send({
            operation: "points",
            positions: positions.slice(0, count * 3),
            spacing,
            eligible: eligible.slice(0, count),
          });
        }
        if (!loaded || !node.numPoints || spacing > settings.resolution) {
          if (node.children.length) {
            queue.complete(node, true);
          } else {
            reasons.add(
              "Some scan regions are coarser than the requested resolution",
            );
            queue.complete(node, false);
          }
        } else {
          queue.complete(node, false);
        }
      },
    });
    const discovery = await send({ operation: "tiles" });
    for (const reason of prepared[0]?.reasons ?? []) reasons.add(reason);
    for (const reason of discovery.reasons ?? []) reasons.add(reason);
    if (!discovery.tiles?.length) {
      reasons.add(
        "No included scan samples were available in the selected scope",
      );
    }
    const tiles = (discovery.tiles ?? []).map((tile) => ({ tile, root: tile }));
    const roots = new Map(
      tiles.map(({ root }) => [root.key, { pending: 1, failed: false }]),
    );
    const processing: NonNullable<ClashRun["processing"]> = {
      nodes: requested,
      failedNodes,
      completedTiles: 0,
      unresolvedTiles: 0,
      complete: false,
      usedCells: [],
    };
    let nextTile = 0;
    await runClashQueue({
      next: () => {
        if (nextTile >= tiles.length) return;
        return tiles[nextTile++];
      },
      concurrency: compute.length,
      control,
      signal: combined,
      run: ({ tile }, slot) =>
        compute[slot]
          .send({
            operation: compute.length === 1 ? "tile" : "compute",
            tile,
            origin: origin.toArray(),
          })
          .catch((error: unknown) => {
            owner.abort(error);
            throw error;
          }),
      commit: async ({ tile, root }, result) => {
        progress(
          `Checking spatial cells (${compute.length} workers): ${processing.completedTiles} processed, ${Math.max(0, tiles.length - nextTile)} queued, ${processing.unresolvedTiles} unresolved`,
        );
        const rootState = roots.get(root.key);
        if (!rootState) throw new Error("Missing spatial queue checkpoint");
        if (result.split) {
          const sizes = tile.min.map((value, axis) => tile.max[axis] - value);
          const axis = sizes.indexOf(Math.max(...sizes));
          if (sizes[axis] > settings.resolution * 2) {
            const midpoint = (tile.min[axis] + tile.max[axis]) / 2;
            const left = structuredClone(tile);
            const right = structuredClone(tile);
            left.key += "/0";
            right.key += "/1";
            left.max[axis] = midpoint;
            right.min[axis] = midpoint;
            tiles.push({ tile: left, root }, { tile: right, root });
            rootState.pending++;
            return;
          }
          rootState.failed = true;
          processing.unresolvedTiles++;
          reasons.add(
            "Some spatial cells exceed available working memory or computation capacity at the requested resolution",
          );
        } else {
          if (compute.length > 1) {
            if (!result.groups) {
              throw new Error("Clash worker returned no cell groups");
            }
            await send({ operation: "merge", tile, groups: result.groups });
          }
          processing.completedTiles++;
        }
        for (const reason of result.reasons ?? []) reasons.add(reason);
        rootState.pending--;
        if (rootState.pending === 0 && !rootState.failed) {
          processing.usedCells.push([
            new Vector3().fromArray(root.min).add(origin).toArray(),
            new Vector3().fromArray(root.max).add(origin).toArray(),
          ]);
        }
      },
    });
    processing.complete = failedNodes === 0 && processing.unresolvedTiles === 0;
    progress("Reconciling findings across spatial cells");
    const result = await send({
      operation: "finish",
      origin: origin.toArray(),
    });
    for (const reason of result.reasons ?? []) reasons.add(reason);
    if (points === 0) {
      reasons.add("No scan samples were available in the selected scope");
    }
    return {
      fingerprint,
      settings,
      hiddenScans,
      candidates: result.candidates ?? [],
      coverage: reasons.size ? "partial" : "complete",
      reasons: [...reasons],
      points,
      processing,
    };
  } finally {
    owner.abort();
    for (const worker of workers) worker.terminate();
    for (const id of leases) client.lodTreeFetcher.releaseNode(id, client);
    client.lodTreeFetcher.removeClient(client);
    indexedDB.deleteDatabase(storeName);
  }
}
